import { isStorageAuthorityReadsFeatureEnabled } from "@/config/runtimeConfig";
import {
  assetIdForAuthorityUrl,
  rememberAuthorityAssetUrl,
} from "@/services/authorityAssetUrlRegistry";
import { storageAuthorityService } from "@/services/storageAuthorityService";

// Item photos live in the storage authority and are shown through short-lived
// signed download URLs. Cards, the item viewer and the feed all ask for the
// same assets, so one resolver signs each asset once, reuses the URL until
// shortly before it expires, and signs again on request when an image fails.

const ASSET_ID_PATTERN = /^[a-f0-9]{64}$/;
// Stop handing out a URL this long before the server says it expires.
const EXPIRY_MARGIN_MS = 30_000;
const MAX_CACHED_ASSETS = 500;

export function createItemMediaUrlResolver({
  reader = storageAuthorityService,
  enabled = isStorageAuthorityReadsFeatureEnabled,
  now = () => Date.now(),
} = {}) {
  // assetId -> { url, expiresAt } | { pending: Promise<string|null> }
  const cache = new Map();

  const remember = (assetId, entry) => {
    cache.delete(assetId);
    cache.set(assetId, entry);
    if (cache.size > MAX_CACHED_ASSETS) {
      cache.delete(cache.keys().next().value);
    }
  };

  const sign = (assetId) => {
    const pending = (async () => {
      try {
        const asset = await reader.createAssetDownloadUrl(assetId);
        if (!asset?.mimeType?.startsWith("image/") || !asset.downloadUrl) {
          cache.delete(assetId);
          return null;
        }
        const expiresAt = Number(asset.expiresAt);
        remember(assetId, {
          url: asset.downloadUrl,
          expiresAt: Number.isFinite(expiresAt) ? expiresAt : 0,
        });
        rememberAuthorityAssetUrl(asset.downloadUrl, assetId);
        return asset.downloadUrl;
      } catch (_error) {
        // Revoked, private, missing or malformed assets are omitted. The item
        // stays usable without its photo and no object path is exposed.
        cache.delete(assetId);
        return null;
      }
    })();
    remember(assetId, { pending });
    return pending;
  };

  const resolveOne = (assetId, { force = false } = {}) => {
    if (typeof assetId !== "string" || !ASSET_ID_PATTERN.test(assetId)) {
      return Promise.resolve(null);
    }
    const cached = cache.get(assetId);
    if (cached?.pending) return cached.pending;
    if (!force && cached?.url && cached.expiresAt - EXPIRY_MARGIN_MS > now()) {
      return Promise.resolve(cached.url);
    }
    return sign(assetId);
  };

  return Object.freeze({
    // Image URLs for the given assets, in order, without the ones that could
    // not be shown. `limit` bounds how many assets are signed.
    async resolveImageUrls(assetIds, { limit = null } = {}) {
      if (!enabled || !Array.isArray(assetIds) || assetIds.length === 0) {
        return Object.freeze([]);
      }
      const wanted =
        Number.isSafeInteger(limit) && limit >= 0
          ? assetIds.slice(0, limit)
          : assetIds;
      const urls = await Promise.all(wanted.map((assetId) => resolveOne(assetId)));
      return Object.freeze(urls.filter(Boolean));
    },

    // A fresh URL for an authority image that stopped loading, or null when
    // the URL is not an authority asset or the asset can no longer be shown.
    async refreshUrl(url) {
      if (!enabled) return null;
      const assetId = assetIdForAuthorityUrl(url);
      if (!assetId) return null;
      return resolveOne(assetId, { force: true });
    },
  });
}

export const itemMediaUrlResolver = createItemMediaUrlResolver();
